import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'

export function HomePage() {
  const { token, user, refresh } = useAuth()
  const nav = useNavigate()
  const [events, setEvents] = useState<any[]>([])
  const [catalog, setCatalog] = useState<any>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    Promise.all([
      api<{ events: any[] }>('/api/events', { token }),
      api('/api/catalog', { token }).catch(() => null),
    ])
      .then(([ev, cat]) => {
        setEvents(ev.events)
        setCatalog(cat)
      })
      .catch(() => {})
  }, [token])

  async function createMma() {
    setBusy(true)
    setError(null)
    try {
      const res = await api<{ competition: { id: string } }>('/api/competitions', {
        method: 'POST',
        token,
        idempotencyKey: `create-mma-${Date.now()}`,
        body: {
          title: catalog?.featuredCard?.event || 'UFC 329 Fight Night',
          template: 'mma-fight-card',
        },
      })
      await refresh()
      nav(`/events/${res.competition.id}`)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  const featured = catalog?.featuredCard

  return (
    <div className="stack">
      <section
        className="hero-fight"
        style={{ minHeight: 260 }}
      >
        <div
          className="bg"
          style={{
            backgroundImage: `url(${catalog?.assets?.hero || '/assets/fighters/event-art.jpg'})`,
          }}
        />
        <div className="veil" />
        <div className="content">
          <div className="eyebrow">Featured fight night</div>
          <h1>{featured?.event || 'UFC 329: McGregor vs. Holloway 2'}</h1>
          <div className="meta">
            <span>{featured?.venue || 'T-Mobile Arena'}</span>
            <span>{featured?.city || 'Paradise, Nevada'}</span>
            <span>{featured?.boutCount || 14} bouts</span>
            <span>$25 demo pool</span>
          </div>
          <p className="muted" style={{ color: '#cbd5e1', maxWidth: 520 }}>
            Full 14-bout card (main · prelims · early) with winner / method / round props, fighter
            photos, live Polymarket odds, and auto-settlement when markets resolve.
          </p>
          {error && <div className="error">{error}</div>}
          <div className="hero-actions">
            <button className="btn" onClick={createMma} disabled={busy} data-testid="create-mma">
              {busy ? 'Opening cage…' : 'Open UFC 329 private event'}
            </button>
            <Link className="btn secondary" to="/discover">
              Browse catalog
            </Link>
          </div>
        </div>
      </section>

      <div className="grid two">
        <section className="panel">
          <h2>Your events</h2>
          {events.length === 0 ? (
            <p className="muted">No events yet. Spin up the featured fight card to start a pool.</p>
          ) : (
            <div className="event-list">
              {events.map((ev) => (
                <div className="event-row" key={ev.id}>
                  <img src={ev.cover || '/assets/mma/server-card-cover/mobile-crop.png'} alt="" />
                  <div>
                    <strong>{ev.title}</strong>
                    <div className="sub">
                      {ev.template} · {ev.status}
                      {ev.boutCount ? ` · ${ev.boutCount} bouts` : ''}
                    </div>
                  </div>
                  <Link className="btn secondary" to={`/events/${ev.id}`}>
                    Open
                  </Link>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="panel">
          <h2>Full card preview</h2>
          <p className="muted" style={{ marginBottom: 10 }}>
            Signed in as <strong>{user?.displayName}</strong>. Demo credits only — not real money.
          </p>
          {['main', 'prelim', 'early'].map((sec) => {
            const list = (catalog?.featuredBouts || []).filter((f: any) => f.section === sec)
            if (!list.length) return null
            const labels: Record<string, string> = {
              main: 'Main card',
              prelim: 'Prelims',
              early: 'Early prelims',
            }
            return (
              <div key={sec} style={{ marginBottom: 10 }}>
                <div className="section-label" style={{ marginBottom: 4 }}>
                  {labels[sec]} · {list.length}
                </div>
                {list.map((f: any) => (
                  <div
                    key={f.fixtureId}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 8,
                      alignItems: 'center',
                      padding: '6px 0',
                      borderBottom: '1px solid var(--field-line)',
                      fontSize: 12.5,
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                      {f.red?.image && (
                        <img
                          src={f.red.image}
                          alt=""
                          style={{ width: 22, height: 28, objectFit: 'cover', borderRadius: 3 }}
                        />
                      )}
                      <span>
                        <strong>{f.red?.name || f.homeEntrantId}</strong>
                        <span className="muted"> vs </span>
                        <strong>{f.blue?.name || f.awayEntrantId}</strong>
                      </span>
                      {f.blue?.image && (
                        <img
                          src={f.blue.image}
                          alt=""
                          style={{ width: 22, height: 28, objectFit: 'cover', borderRadius: 3 }}
                        />
                      )}
                    </span>
                    <span className="muted" style={{ flexShrink: 0 }}>
                      {f.billing || f.weightClass}
                    </span>
                  </div>
                ))}
              </div>
            )
          })}
          <div className="row" style={{ marginTop: 12 }}>
            <span className="chip">
              <span className="dot" />
              server authoritative
            </span>
            <span className="chip">polymarket odds</span>
            <span className="chip">auto-settle</span>
          </div>
        </section>
      </div>
    </div>
  )
}
